import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import Stars from '@/pages/product-detail/components/Stars';

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  price: number;
  original_price: number | null;
  image: string;
  badge: string;
}

export default function EditorsPicks() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('products').select('id,name,brand,rating,reviews_count,price,original_price,image,badge').order('rating', { ascending: false }).then(({ data }) => {
      if (data) setProducts(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900 tracking-tight">
                Editor&rsquo;s Picks
              </h1>
              <p className="mt-3 text-lg text-ink-600 max-w-2xl leading-relaxed">
                Every product here has been hands-on tested by our editorial team. No algorithms, no sponsored placements. Just honest, informed recommendations from people who care about great products.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {['https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20person%20with%20warm%20neutral%20expression%20on%20simple%20light%20background%20editorial%20minimal&width=80&height=80&seq=editor-avatar-01&orientation=squarish', 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20person%20with%20friendly%20smile%20on%20simple%20light%20background%20editorial%20minimal&width=80&height=80&seq=editor-avatar-02&orientation=squarish', 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20person%20looking%20confident%20on%20simple%20light%20background%20editorial%20minimal&width=80&height=80&seq=editor-avatar-03&orientation=squarish'].map((src, i) => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-white overflow-hidden bg-ink-100" style={{ zIndex: 3 - i }}>
                    <img src={src} alt="Editor" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <span className="text-sm text-ink-500">Tested by our team of 12 experts</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
            {products.map(p => (
              <div key={p.id} className="product-card group relative bg-white rounded-2xl border border-ink-100 overflow-hidden hover:border-cta-200 transition">
                <Link to={`/product/${p.id}`} className="relative aspect-[4/3] bg-ink-50 overflow-hidden block">
                  <img src={p.image} alt={p.name} className="product-image w-full h-full object-cover object-top group-hover:scale-105 transition duration-500" />
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-ink-900/80 backdrop-blur-sm text-white text-[11px] font-bold">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-cta-400 text-xs"></i>
                    </span>
                    {p.badge}
                  </div>
                </Link>
                <div className="p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                  <h3 className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2 min-h-[40px]">
                    <Link to={`/product/${p.id}`} className="hover:text-cta-600 transition">{p.name}</Link>
                  </h3>
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <Stars rating={p.rating} />
                    <span className="font-semibold text-ink-800">{p.rating}</span>
                    <span className="text-ink-400">({p.reviews_count.toLocaleString()})</span>
                  </div>
                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <p className="font-display text-xl font-bold text-ink-900">${p.price}</p>
                      {p.original_price != null && p.original_price > p.price && (
                        <p className="text-[11px] text-ink-400 line-through">${p.original_price}</p>
                      )}
                    </div>
                    <Link
                      to={`/product/${p.id}`}
                      className="h-9 px-3 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-xs font-semibold whitespace-nowrap flex items-center gap-1 transition"
                    >
                      View Review
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-base"></i>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 md:p-10 rounded-2xl bg-white border border-ink-100">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-1">
                <h2 className="font-display text-2xl font-bold text-ink-900">Our Testing Methodology</h2>
                <p className="mt-3 text-ink-600 leading-relaxed max-w-xl">
                  We don&rsquo;t just read spec sheets. Our editors spend weeks, sometimes months, using products in real-world conditions before making a recommendation. Each pick goes through at least 40 hours of hands-on testing across multiple scenarios.
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-timer-flash-line', label: 'Min. 40 hours', desc: 'Of hands-on testing per product' },
                    { icon: 'ri-user-search-line', label: 'Multiple testers', desc: 'Different perspectives, one consensus' },
                    { icon: 'ri-shield-check-line', label: 'No sponsor bias', desc: 'We buy or borrow, never accept freebies' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-lg bg-cta-50 flex items-center justify-center flex-shrink-0 text-cta-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-ink-900">{item.label}</p>
                        <p className="text-xs text-ink-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full md:w-72 shrink-0">
                <div className="bg-ink-50 rounded-xl p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Our promise</p>
                  <p className="mt-2 text-sm text-ink-700 leading-relaxed">
                    If we wouldn&rsquo;t recommend it to our mom, we won&rsquo;t put it on this page. Simple as that.
                  </p>
                  <p className="mt-3 text-xs text-ink-400">The BuyWize Editorial Team</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}